import { getUserUid } from "./userInfo";
import { GET_WEB_SOCKET_API } from "./requestUrl";
import { Message } from "element-ui"; // 假设使用element-ui做提示

class WebSocketClient {
  constructor(url, options = {}, token = null) {
    this.url = url;
    this.token = token;
    this.options = {
      heartbeatInterval: 30000, // 心跳间隔，默认30秒 30000
      reconnectInterval: 5000, // 重连间隔，默认5秒
      reconnectAttempts: 5, // 重连尝试次数，默认5次
      ...options,
    };

    this.ws = null;
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
    this.reconnectCount = 0;
    this.messageCallbacks = new Set();
    this.closeCallbacks = new Set();
    this.errorCallbacks = new Set();
    this.messageBuffer = [];
    this.bufferTimeout = null;
    this.bufferInterval = 2000; // 缓冲时间为2秒
    this.lastMessageTime = 0;
    this.maxBufferSize = 10; // 缓冲区最大消息数
    this.isFirstMessage = true; // 标记是否是第一条消息
  }

  // 连接WebSocket
  connect() {
    console.log(this.url ||
      GET_WEB_SOCKET_API +
      "socket/model?uid=" +
      (this.token || encodeURIComponent(getUserUid())))
    try {
      this.ws = new WebSocket(
        this.url ||
        GET_WEB_SOCKET_API +
        "socket/model?uid=" +
        (this.token || encodeURIComponent(getUserUid()))
      );
      this.bindEvents();
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // 绑定WebSocket事件
  bindEvents() {
    this.ws.onopen = () => {
      console.log("WebSocket连接成功");
      this.reconnectCount = 0;
      this.startHeartbeat();
      this.isFirstMessage = true; // 重置第一条消息标记
    };

    this.ws.onmessage = (event) => {
      const message = this.parseMessage(event.data);
      // 如果是心跳响应，不触发消息回调
      if (message === "heartbeat") {
        return;
      }

      // 如果是第一条消息，立即处理
      if (this.isFirstMessage) {
        this.processMessage(message);
        this.isFirstMessage = false;
      } else {
        // 否则添加到缓冲区
        this.bufferMessage(message);
      }
    };

    this.ws.onclose = (event) => {
      console.log("WebSocket连接关闭:", event.code, event.reason);
      this.stopHeartbeat();
      this.closeCallbacks.forEach((callback) => callback(event));
      this.reconnect();
    };

    this.ws.onerror = (error) => {
      console.error("WebSocket错误:", error);
      this.errorCallbacks.forEach((callback) => callback(error));
    };
  }

  // 处理单条消息
  processMessage(message) {
    this.messageCallbacks.forEach((callback) => {
      try {
        callback(message.content);
      } catch (error) {
        console.error("消息处理错误:", error);
      }
    });
  }

  // 缓冲消息
  bufferMessage(message) {
    try {
      // 记录最后一条消息的时间
      this.lastMessageTime = Date.now();
      
      // 将消息添加到缓冲区
      this.messageBuffer.push(message);
      
      // 如果缓冲区消息数量超过最大值，处理第一条消息
      if (this.messageBuffer.length >= this.maxBufferSize) {
        this.processFirstMessage();
      }
      
      // 重置定时器
      this.resetBufferTimer();
    } catch (error) {
      console.error("缓冲消息错误:", error);
    }
  }
  
  // 重置缓冲定时器
  resetBufferTimer() {
    clearTimeout(this.bufferTimeout);
    this.bufferTimeout = setTimeout(() => {
      // 如果自上次消息已经过了2秒，处理所有剩余消息
      if (Date.now() - this.lastMessageTime >= this.bufferInterval) {
        this.processAllMessages();
      } else {
        // 否则继续等待
        this.resetBufferTimer();
      }
    }, this.bufferInterval);
  }

  // 处理第一条消息
  processFirstMessage() {
    if (this.messageBuffer.length === 0) return;
    
    // 按time字段排序
    this.messageBuffer.sort((a, b) => {
      const timeA = a.time || 0;
      const timeB = b.time || 0;
      return timeA - timeB;
    });
    
    // 取出第一条消息
    const message = this.messageBuffer.shift();
    
    // 处理消息
    this.processMessage(message);
  }

  // 处理所有消息
  processAllMessages() {
    if (this.messageBuffer.length === 0) {
      this.bufferTimeout = null;
      return;
    }
    
    // 按time字段排序
    this.messageBuffer.sort((a, b) => {
      const timeA = a.time || 0;
      const timeB = b.time || 0;
      return timeA - timeB;
    });
    
    // 处理所有缓冲的消息
    this.messageBuffer.forEach(message => {
      this.processMessage(message);
    });
    
    // 清空缓冲区和定时器
    this.messageBuffer = [];
    this.bufferTimeout = null;
  }

  // 发送消息
  send(data) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("WebSocket未连接"));
    }

    try {
      const message = typeof data === "string" ? data : JSON.stringify(data);
      this.ws.send(message);
      return Promise.resolve();
    } catch (error) {
      console.error("发送消息错误:", error);
      return Promise.reject(error);
    }
  }

  // 关闭连接
  close() {
    if (this.ws) {
      this.stopHeartbeat();
      clearTimeout(this.reconnectTimer);
      clearTimeout(this.bufferTimeout);
      this.bufferTimeout = null;
      this.messageBuffer = [];
      this.isFirstMessage = true;
      this.ws.close();
      this.ws = null;
    }
  }

  // 重新连接
  reconnect() {
    if (this.reconnectCount >= this.options.reconnectAttempts) {
      Message.warning("您长时间未操作，请刷新页面");
      console.warn("达到最大重连次数");
      return;
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      console.warn(`第${this.reconnectCount + 1}次重连`);
      this.reconnectCount++;
      this.connect();
    }, this.options.reconnectInterval);
  }

  // 开始心跳
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: "heartbeat", data: "ping" });
      console.info("发送心跳");
    }, this.options.heartbeatInterval);
  }

  // 停止心跳
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // 解析消息
  parseMessage(data) {
    try {
      return typeof data === "string" ? JSON.parse(data) : data;
    } catch (error) {
      return data;
    }
  }

  // 添加消息监听器
  onMessage(callback) {
    this.messageCallbacks.add(callback);
    return () => this.messageCallbacks.delete(callback);
  }

  // 添加关闭监听器
  onClose(callback) {
    this.closeCallbacks.add(callback);
    return () => this.closeCallbacks.delete(callback);
  }

  // 添加错误监听器
  onError(callback) {
    this.errorCallbacks.add(callback);
    return () => this.errorCallbacks.delete(callback);
  }

  // 判断当前连接是否关闭
  isConnectionClosed() {
    return (
      !this.ws ||
      this.ws.readyState === WebSocket.CLOSED ||
      this.ws.readyState === WebSocket.CLOSING
    );
  }
}

export default WebSocketClient;
